import "./Operations.css";
import { ApiError } from "@/api/client";
import { useEffect, useRef, useState } from "react";
import { RunSheet } from "@/components/run/RunSheet";
import type { RoundTransport } from "@/components/rounds/model";
export interface JamItem {
  attempt_id: string;
  heat_rider_id: string;
  item_no?: number;
  attempt_number?: number;
  label: string;
  status?: string;
  queued?: boolean;
}
export interface JamRider {
  id: string;
  name: string;
  attempts?: { attempt_number: number; counted?: boolean }[];
}
export function JamQueue(props: {
  competitionId: string;
  heatId: string;
  principalKey: string;
  version: number;
  bestN: number;
  riders: JamRider[];
  transport: RoundTransport;
  disabled: boolean;
  online: boolean;
  queued?: JamItem[];
  append: (payload: {
    heat_rider_id: string;
    label: string;
    ts_ms: number;
    client_item_id: string;
    expected_version: number;
    item_no: number;
  }) => Promise<void>;
}) {
  const [snapshot, setSnapshot] = useState<{
    scope: string;
    items: JamItem[];
    version: number;
  } | null>(null);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [open, setOpen] = useState(false),
    [rider, setRider] = useState(""),
    [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  // Accepted local ordinals survive the gap between replay removing queued ops
  // and the canonical queue read completing. Reusing one would merge two tricks.
  const acceptedOrdinals = useRef(new Map<string, number>());
  const scope = `${props.competitionId}:${props.principalKey}:${props.heatId}`;
  const latest = useRef(scope);
  latest.current = scope;
  const submission = useRef<{ signature: string; id: string; at: number; itemNo: number } | null>(
    null,
  );
  useEffect(() => {
    setBusy(false);
    setOpen(false);
    setError("");
    setLoadError("");
    submission.current = null;
    try {
      const raw = localStorage.getItem(`scorio.jam-items:${scope}`);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached.scope === scope && Array.isArray(cached.items)) setSnapshot(cached);
      }
    } catch {
      /* Server refresh repairs evicted cache. */
    }
  }, [scope]);
  useEffect(() => {
    let alive = true;
    if (props.online)
      void props.transport
        .get<{ items: JamItem[]; version: number }>(
          `/api/competitions/${props.competitionId}/heats/${props.heatId}/jam-queue`,
        )
        .then((data) => {
          if (alive) {
            const items = data.items.map(
              ({ attempt_id, heat_rider_id, item_no, attempt_number, label, status }) => ({
                attempt_id,
                heat_rider_id,
                item_no,
                attempt_number,
                label,
                status,
              }),
            );
            const next = { scope, items, version: data.version };
            setSnapshot(next);
            try {
              localStorage.setItem(`scorio.jam-items:${scope}`, JSON.stringify(next));
            } catch {
              /* In-memory queue metadata still works while this screen stays open. */
            }
            setLoadError("");
          }
        })
        .catch((cause) => {
          if (alive)
            setLoadError(cause instanceof Error ? cause.message : "Could not load attempts.");
        });
    return () => {
      alive = false;
    };
  }, [
    scope,
    props.competitionId,
    props.heatId,
    props.version,
    props.transport,
    props.online,
    refresh,
  ]);
  const items = [...(snapshot?.scope === scope ? snapshot.items : []), ...(props.queued ?? [])];
  const disabled = props.disabled || snapshot?.scope !== scope || props.riders.length === 0;
  return (
    <section aria-label="Jam attempts" className="operation-jam">
      <h2>
        Jam attempts <small>Best {props.bestN} count</small>
      </h2>
      {(error || loadError) && <p role="alert">{error || loadError}</p>}
      <button
        disabled={disabled || busy}
        onClick={() => {
          setRider(props.riders[0]?.id ?? "");
          setLabel("");
          submission.current = null;
          setOpen(true);
        }}
      >
        Add attempt
      </button>
      {!props.online && (
        <p role="status">
          {props.disabled
            ? "Offline — reconnect to add attempts."
            : snapshot?.scope !== scope
              ? "Open this heat online before adding attempts offline."
              : "Attempts are saved on this device and added after reconnect."}
        </p>
      )}
      {props.riders.map((row) => (
        <div key={row.id}>
          <h3>{row.name}</h3>
          <ul>
            {items
              .filter((item) => item.heat_rider_id === row.id)
              .map((item) => (
                <li key={item.attempt_id}>
                  {item.item_no ? `Item ${item.item_no} · ` : ""}
                  {item.label} ·{" "}
                  {item.queued
                    ? "Queued"
                    : row.attempts?.find(
                          (attempt) => attempt.attempt_number === item.attempt_number,
                        )?.counted
                      ? `Counts in best ${props.bestN}`
                      : (item.status?.replaceAll("_", " ") ?? "Awaiting score")}
                </li>
              ))}
          </ul>
        </div>
      ))}
      {open && (
        <RunSheet title="Add attempt" close={() => setOpen(false)}>
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              if (
                disabled ||
                busy ||
                latest.current !== scope ||
                !props.riders.some((row) => row.id === rider)
              )
                return;
              const signature = JSON.stringify([scope, rider, label.trim()]);
              if (submission.current?.signature !== signature)
                submission.current = {
                  signature,
                  id: crypto.randomUUID(),
                  at: Date.now(),
                  itemNo:
                    Math.max(
                      0,
                      acceptedOrdinals.current.get(`${scope}:${rider}`) ?? 0,
                      ...items
                        .filter((item) => item.heat_rider_id === rider)
                        .map((item) => item.item_no ?? 0),
                    ) + 1,
                };
              setBusy(true);
              setError("");
              const announced = submission.current;
              try {
                await props.append({
                  heat_rider_id: rider,
                  label: label.trim(),
                  ts_ms: announced.at,
                  client_item_id: announced.id,
                  expected_version: Math.max(props.version, snapshot?.version ?? 0),
                  item_no: announced.itemNo,
                });
                const key = `${scope}:${rider}`;
                acceptedOrdinals.current.set(
                  key,
                  Math.max(acceptedOrdinals.current.get(key) ?? 0, announced.itemNo),
                );
                if (latest.current === scope) setOpen(false);
              } catch (cause) {
                if (latest.current === scope) {
                  if (cause instanceof ApiError && cause.status === 409) {
                    submission.current = null;
                    setSnapshot(null);
                    setRefresh((value) => value + 1);
                  }
                  setError(cause instanceof Error ? cause.message : "Could not add attempt.");
                }
              } finally {
                if (latest.current === scope) setBusy(false);
              }
            }}
          >
            <label>
              Participant
              <select value={rider} onChange={(event) => setRider(event.target.value)}>
                {props.riders.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Attempt label
              <input
                required
                maxLength={120}
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            </label>
            {error && <p role="alert">{error}</p>}
            <button disabled={disabled || busy || !rider || !label.trim()} type="submit">
              Save attempt
            </button>
          </form>
        </RunSheet>
      )}
    </section>
  );
}
